import { useEffect, useRef, useState } from "react";
import type { ConversationSource, SessionSort } from "../types";

/** Filtro de origem na sidebar: "" = todas. */
export type SourceFilter = "" | ConversationSource;

interface Props {
  pinnedOnly: boolean;
  onPinnedOnlyChange: (v: boolean) => void;
  dateFrom: string;
  onDateFromChange: (v: string) => void;
  dateTo: string;
  onDateToChange: (v: string) => void;
  sessionSort: SessionSort;
  onSessionSortChange: (v: SessionSort) => void;
  sourceFilter: SourceFilter;
  onSourceFilterChange: (v: SourceFilter) => void;
}

const SORT_LABEL: Record<SessionSort, string> = {
  recent: "Mais recentes",
  oldest: "Mais antigas",
  cost_desc: "Maior custo",
  tokens_desc: "Mais tokens",
};

const SOURCE_LABEL: Record<SourceFilter, string> = {
  "": "Todas",
  web: "Manual (painel)",
  api: "API",
};

/** Filtros avançados da busca na sidebar: período, só fixadas, ordenação. */
export function SearchFiltersPopover({
  pinnedOnly,
  onPinnedOnlyChange,
  dateFrom,
  onDateFromChange,
  dateTo,
  onDateToChange,
  sessionSort,
  onSessionSortChange,
  sourceFilter,
  onSourceFilterChange,
}: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  // "web" (só manuais) é o padrão, então não conta como filtro ativo.
  const active = pinnedOnly || Boolean(dateFrom) || Boolean(dateTo) || sessionSort !== "recent" || sourceFilter !== "web";

  const clear = () => {
    onPinnedOnlyChange(false);
    onDateFromChange("");
    onDateToChange("");
    onSessionSortChange("recent");
    onSourceFilterChange("web");
  };

  return (
    <div className="search-filters-wrap" ref={ref}>
      <button
        type="button"
        className={`search-filters-btn ${active ? "active" : ""}`}
        onClick={() => setOpen((v) => !v)}
        title="Filtros avançados"
        aria-label="Filtros avançados"
        aria-expanded={open}
      >
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M2 3h12M4.5 8h7M7 13h2" />
        </svg>
        {active && <span className="search-filters-dot" aria-hidden />}
      </button>
      {open && (
        <div className="search-filters-menu">
          <div className="search-filters-row">
            <label className="search-filters-label">De</label>
            <input type="date" value={dateFrom} onChange={(e) => onDateFromChange(e.target.value)} />
          </div>
          <div className="search-filters-row">
            <label className="search-filters-label">Até</label>
            <input type="date" value={dateTo} onChange={(e) => onDateToChange(e.target.value)} />
          </div>
          <div className="search-filters-row">
            <label className="search-filters-label">Origem</label>
            <select value={sourceFilter} onChange={(e) => onSourceFilterChange(e.target.value as SourceFilter)}>
              {(Object.keys(SOURCE_LABEL) as SourceFilter[]).map((k) => (
                <option key={k} value={k}>
                  {SOURCE_LABEL[k]}
                </option>
              ))}
            </select>
          </div>
          <div className="search-filters-row">
            <label className="search-filters-label">Ordenar por</label>
            <select value={sessionSort} onChange={(e) => onSessionSortChange(e.target.value as SessionSort)}>
              {(Object.keys(SORT_LABEL) as SessionSort[]).map((k) => (
                <option key={k} value={k}>
                  {SORT_LABEL[k]}
                </option>
              ))}
            </select>
          </div>
          <label className="search-filters-checkbox">
            <input type="checkbox" checked={pinnedOnly} onChange={(e) => onPinnedOnlyChange(e.target.checked)} />
            Somente fixadas
          </label>
          {active && (
            <button type="button" className="search-filters-clear" onClick={clear}>
              Limpar filtros
            </button>
          )}
        </div>
      )}
    </div>
  );
}
